import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { useGetPublicEvents, getPublicEventPriceLabel, type PublicEventItem } from '../api/events';
import { FiCalendar, FiMapPin, FiList, FiGlobe, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';

export const Route = createFileRoute('/events/public')({
  component: PublicEventsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(iso));
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function PublicEventCard({ event }: { event: PublicEventItem }) {
  const heroImg = event.featured_image?.file_url ?? event.featured_image?.image;
  const locationStr = [event.city, event.country].filter(Boolean).join(', ');
  const price = getPublicEventPriceLabel(event);

  return (
    <Link to="/events/$eventId" params={{ eventId: String(event.id) }}>
      <div className="group relative bg-zinc-900 rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-200 hover:scale-[1.02] cursor-pointer">
        <div className="relative w-full aspect-square bg-zinc-800 overflow-hidden">
          {heroImg ? (
            <img
              src={heroImg}
              alt={event.event_title ?? ''}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FiCalendar size={32} className="text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-linear-to-b from-black/40 to-transparent" />
        </div>
        <div className="p-3 space-y-1.5">
          <p className="text-white text-[13px] font-bold leading-tight line-clamp-2">
            {event.event_title ?? 'Untitled Event'}
          </p>
          {locationStr && (
            <p className="text-zinc-400 text-[12px] flex items-center gap-1 truncate">
              <FiMapPin size={11} className="shrink-0" />
              {locationStr}
            </p>
          )}
          {event.start && (
            <p className="text-zinc-500 text-[11px] flex items-center gap-1">
              <FiCalendar size={11} className="shrink-0" />
              {formatDate(event.start)}
            </p>
          )}
          {price && (
            <span
              className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-md ${
                price.variant === 'free' ? 'bg-yellow-400 text-black' : 'bg-white text-black'
              }`}
            >
              {price.text}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-zinc-900 rounded-2xl overflow-hidden animate-pulse">
          <div className="aspect-square bg-zinc-800" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-zinc-700 rounded w-4/5" />
            <div className="h-3 bg-zinc-800 rounded w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PublicEventsPage() {
  const navigate = useNavigate();

  const [allEvents, setAllEvents] = useState<PublicEventItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, isLoading, isError, refetch } = useGetPublicEvents(currentPage, 12);

  useEffect(() => {
    if (!data) return;
    setAllEvents((prev) => (currentPage === 1 ? data.events : [...prev, ...data.events]));
    setHasMore(!!data.next);
    setTotalCount(data.count);
    setIsLoadingMore(false);
  }, [data, currentPage]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setCurrentPage((p) => p + 1);
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex items-center gap-3 mt-2 mb-8">
          <button
            onClick={() => navigate({ to: '/events' })}
            className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-all shrink-0"
            aria-label="Back"
          >
            <FiArrowLeft size={18} />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/8 text-zinc-400 shrink-0">
            <FiGlobe size={17} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white">Public Events</h1>
            <p className="text-zinc-500 text-sm mt-0.5">
              {totalCount > 0 ? `${totalCount} upcoming events` : 'Upcoming events around you'}
            </p>
          </div>
        </div>

        {/* Loading — first load */}
        {isLoading && allEvents.length === 0 && <Skeleton count={12} />}

        {/* Error */}
        {isError && allEvents.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <FiAlertCircle size={40} className="text-zinc-600" />
            <p className="text-zinc-400 text-sm">Failed to load events. Please try again.</p>
            <Button onClick={() => refetch()} variant="secondary" className="rounded-full px-6">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && allEvents.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <FiCalendar size={40} className="text-zinc-600" />
            <p className="text-zinc-400 text-sm">No public events found.</p>
          </div>
        )}

        {/* Grid */}
        {allEvents.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {allEvents.map((event) => (
                <PublicEventCard key={event.id} event={event} />
              ))}
            </div>

            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/8 hover:border-white/15 bg-white/3 hover:bg-white/5 text-zinc-400 hover:text-white text-[13px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? (
                  <span className="animate-pulse">Loading…</span>
                ) : (
                  <>
                    <FiList size={15} />
                    Load More Events
                  </>
                )}
              </button>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
